/**
 * reCAPTCHA v3 token collection for the portal page.
 *
 * The v3 script is loaded by home/template/v2/homeTemplateV2.jsp and the site key is passed through on
 * the mount element, so it is not duplicated here. Mirrors the call registration already makes
 * (home/template/v2/register.jsp).
 */

// School networks fetch this third party script through a filtering proxy, so five seconds was
// short enough to time out legitimate users on a slow morning.
const RECAPTCHA_TIMEOUT_MS = 15000;

let siteKey = null;

export const setRecaptchaSiteKey = (key) => {
    siteKey = key;
};

/**
 * Resolves to a token, or to null when reCAPTCHA is unavailable. Never rejects.
 *
 * A null means the caller must NOT submit: the server refuses a request with no token, so posting
 * anyway produces a rejection the user cannot act on. That combination was most of the portal's
 * captcha errors - 755 of 946 in twelve hours, one user retrying 36 times - because the failure was
 * invisible on this side and fatal on the other.
 *
 * The reason is logged so the two populations can be told apart: a script that never loaded (school
 * proxy, extension, offline) against one that loaded and failed to execute.
 */
export const getRecaptchaToken = (action) => new Promise((resolve) => {
    if (!siteKey) {
        console.error('reCAPTCHA site key is missing - check data-recaptcha-site-key on the mount element');
        resolve(null);
        return;
    }

    if (typeof window.grecaptcha === 'undefined' || !window.grecaptcha.ready) {
        console.error('reCAPTCHA script has not loaded - it may be blocked by a network filter or extension');
        resolve(null);
        return;
    }

    // Don't let a slow or blocked reCAPTCHA hang the form.
    const timeout = setTimeout(() => {
        console.error('reCAPTCHA did not respond within ' + RECAPTCHA_TIMEOUT_MS + 'ms');
        resolve(null);
    }, RECAPTCHA_TIMEOUT_MS);

    try {
        window.grecaptcha.ready(() => {
            window.grecaptcha.execute(siteKey, {action})
                .then((token) => {
                    clearTimeout(timeout);
                    resolve(token);
                })
                .catch((error) => {
                    console.error('reCAPTCHA execute failed', error);
                    clearTimeout(timeout);
                    resolve(null);
                });
        });
    } catch (error) {
        console.error('reCAPTCHA is unavailable', error);
        clearTimeout(timeout);
        resolve(null);
    }
});
